import { actionPlansServices } from '../../../../service/actionPlans'
import { storesService } from '../../../../service/stores'
import { UserService } from '../../../../service/user'
import { httpFetch } from '../../../../utils/crud'
import { AutomaticTempByIdResponse } from '../../../../service/actionPlans/response'
import { EvidencesTypeValueEnum } from '../../../../utils/enums/evidences'
import { Roles } from '../../../../utils/enums'
import { SaveAutomaticTempItemsBody } from '../../../../service/actionPlans/body'
import { AvailableUser, InternalUser, PlanToGenerateInfo, Question, Evidence, CreateActionPlanData, StoreFilterOption, UserFilters } from './types'

const mapMode = (response: AutomaticTempByIdResponse): PlanToGenerateInfo['mode'] => {
  if (response.assignedQuestionnaireId) return 'STORE'
  if (response.userQuestionnaireId && response.storeId) return 'USER_WITH_STORE'
  return 'USER_WITHOUT_STORE'
}

const mapEvidence = (
  evidence: AutomaticTempByIdResponse['items'][0]['evidences'][0],
  index: number
): Evidence | null => {
  if (evidence.comments?.length) {
    return {
      id: index,
      type: 'comment',
      src: '',
      name: evidence.comments.join('\n'),
      comments: evidence.comments,
    }
  }

  switch (evidence.evidenceTypeId) {
    case EvidencesTypeValueEnum.IMAGE:
    case EvidencesTypeValueEnum.CAMERA:
      return {
        id: index,
        type: 'image',
        src: evidence.url ?? '',
        name: evidence.originalname ?? '',
      }
    case EvidencesTypeValueEnum.VIDEO:
    case EvidencesTypeValueEnum.VIDEO_GALLERY:
      return {
        id: index,
        type: 'video',
        src: evidence.url ?? '',
        name: evidence.originalname ?? '',
      }
    case EvidencesTypeValueEnum.SIGN:
      return {
        id: index,
        type: 'sign',
        src: evidence.url ?? '',
        name: evidence.originalname ?? '',
      }
    case EvidencesTypeValueEnum.DOCUMENT:
      return {
        id: index,
        type: 'documents',
        src: evidence.url ?? '',
        name: evidence.originalname ?? '',
      }
    case EvidencesTypeValueEnum.COMMENT:
    case EvidencesTypeValueEnum.OBSERVATION_COMMENT:
      return {
        id: index,
        type: 'comment',
        src: '',
        name: evidence.url ?? evidence.originalname ?? '',
      }
    default:
      return null
  }
}

const mapEvidences = (
  evidences: AutomaticTempByIdResponse['items'][0]['evidences']
): Evidence[] => {
  return evidences
    .map((evidence, index) => mapEvidence(evidence, index))
    .filter((evidence): evidence is Evidence => evidence !== null)
}

const mapQuestions = (items: AutomaticTempByIdResponse['items']): Question[] => {
  return items.map((item) => {
    const selectedAnswers = item.question.answers.filter((answer) => answer.selected)

    return {
      id: item.id,
      question: item.question.title,
      answer: selectedAnswers.map((answer) => answer.title).join(' / '),
      fullAnswer: selectedAnswers[0]?.fullAnswer,
      evidences: mapEvidences(item.evidences),
    }
  })
}

const mapEvidenceTypeToEnum = (type: Evidence['type']): number => {
  switch (type) {
    case 'image':
      return EvidencesTypeValueEnum.IMAGE
    case 'video':
      return EvidencesTypeValueEnum.VIDEO
    case 'sign':
      return EvidencesTypeValueEnum.SIGN
    case 'documents':
      return EvidencesTypeValueEnum.DOCUMENT
    case 'comment':
      return EvidencesTypeValueEnum.COMMENT
    default:
      return EvidencesTypeValueEnum.IMAGE
  }
}

const getFullName = (user: { firstName?: string; lastName?: string }) =>
  `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()

const fetchHierarchyUsers = async (query?: string): Promise<AvailableUser[]> => {
  const response = await UserService(httpFetch).getHierarchyByQuery(query)

  if (response.errors) return []

  const users = Array.isArray(response) ? response : []

  return users.map((user) => ({
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    fullName: getFullName(user),
    roleId: user.roleId,
    role: typeof user.role === 'string' ? user.role : user.role?.description ?? '',
    userTypeId: user.userTypeId,
    userType: typeof user.userType === 'string' ? user.userType : user.userType?.description ?? '',
    email: user.email,
    picture: user.picture,
  }))
}

export const getAvailableUsersForActionPlan = async (filters?: UserFilters): Promise<AvailableUser[]> => {
  const params = new URLSearchParams()
  params.append('rolesId', filters?.roles?.join(',') ?? `${Roles.STORE},${Roles.PARTNER}`)
  if (filters?.userTypes?.length) params.append('userTypesId', filters.userTypes.join(','))
  if (filters?.stores?.length) params.append('storesId', filters.stores.join(','))
  const query = params.toString()
  return fetchHierarchyUsers(query || undefined)
}

export const getStoresForFilter = async (): Promise<StoreFilterOption[]> => {
  const response = await storesService(httpFetch).getAllStoresWithAgents()

  if (response.errors) return []

  const stores = Array.isArray(response) ? response : []

  return stores.map((store) => ({
    id: store.id,
    name: store.name,
    keyCode: store.keyCode ?? '',
    label: `${store.name} - ${store.keyCode ?? ''}`,
    value: String(store.id),
  }))
}

export const getInternalUsers = async (): Promise<InternalUser[]> => {
  const users = await fetchHierarchyUsers()
  return users.map((user) => ({
    id: user.id,
    fullName: user.fullName,
    email: user.email,
  }))
}

export const getPlanToGenerateById = async (id: number): Promise<PlanToGenerateInfo | null> => {
  const response = await actionPlansServices(httpFetch).getAutomaticTempById(id)

  if (response.errors) return null

  return {
    id: response.id,
    questionnaireName: response.questionnairName,
    mode: mapMode(response),
    store: response.store
      ? {
          id: response.store.id,
          name: response.store.name,
          keyCode: response.store.keyCode,
          managerName: response.store.userManageName,
        }
      : undefined,
    questions: mapQuestions(response.items),
  }
}

export const buildCreateActionPlanPayload = (
  planInfo: PlanToGenerateInfo,
  data: CreateActionPlanData
): SaveAutomaticTempItemsBody => ({
  title: data.title,
  description: data.description,
  userAssignId: planInfo.mode === 'USER_WITHOUT_STORE' ? data.selectedUser : null,
  itemsSelected: data.selectedQuestions.map((questionId) => {
    const question = planInfo.questions?.find((q) => q.id === questionId)
    const selectedEvidenceIds = data.selectedEvidences
      .filter((item) => item.questionId === questionId)
      .map((item) => item.evidenceId)

    return {
      id: questionId,
      evidences: question?.evidences
        .filter((ev) => selectedEvidenceIds.includes(ev.id))
        .map((ev) => {
          if (ev.comments?.length) {
            return { comments: ev.comments }
          }
          return {
            url: ev.src,
            originalname: ev.name,
            evidenceTypeId: mapEvidenceTypeToEnum(ev.type),
          }
        }) ?? [],
    }
  }),
  actionPlanAutomaticTempId: planInfo.id,
  usersNotified: {
    users: data.selectedInternalUsers,
    emailsExternal: data.externalEmails,
  },
})
